import React, { FC } from "react";

import { brand } from "../theme/brands";

const Section: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-bold mb-3">{title}</h2>
    <div className="text-neutral-700 leading-relaxed space-y-3">{children}</div>
  </section>
);

// Required by both app stores (and good practice for the website). Content
// mirrors what the platform actually collects — keep it in sync with the
// registration form and the store data-safety declarations.
const Privacy: FC = () => (
  <div className="max-w-2xl mx-auto px-4 my-8 md:my-12">
    <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10">
      <h1 className="text-2xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-neutral-500 mb-8">
        {brand.name} — last updated 17 August 2026
      </p>

      <Section title="Who we are">
        <p>
          {brand.name} provides online registration for running and athletic events organised by us and by the event
          organisers we work with. This policy covers the {brand.name} website and mobile apps.
        </p>
      </Section>

      <Section title="What we collect">
        <p>
          <b>When you register for an event:</b> your name, email address, date of birth, gender, phone number,
          locality/residence and (optionally) running club, plus the event and distance you signed up for. This is the
          information the event organiser needs to run the race — start lists, categories, results and race-day
          contact.
        </p>
        <p>
          <b>When you pay:</b> payments are processed by Stripe. Your card details go directly to Stripe and never
          touch our servers; we receive only a payment confirmation and reference. See{" "}
          <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
            Stripe's privacy policy
          </a>
          .
        </p>
        <p>
          <b>If you are an event organiser using the admin portal:</b> your email address and, on the mobile app, a
          push-notification token for your device so we can notify you of new registrations.
        </p>
        <p>We don't collect location data, advertising identifiers or analytics profiles, and we don't sell data.</p>
      </Section>

      <Section title="How we use it">
        <p>
          Registration details are shared with the organiser of the event you entered and used to administer the event
          (confirmation emails, start lists, categories, results and event updates). We don't use your details for
          unrelated marketing.
        </p>
      </Section>

      <Section title="Where it lives and how long">
        <p>
          Data is stored in our database (MongoDB Atlas, EU region) and emails are sent via Amazon SES. Registration
          records are kept for the administration of the event and our accounting obligations; push tokens are deleted
          when you sign out of the app or when your device stops accepting notifications.
        </p>
      </Section>

      <Section title="Your rights">
        <p>
          Under the GDPR you can ask for a copy of your data, ask us to correct it, or ask us to delete it (subject to
          records we must keep for completed paid entries). Email{" "}
          <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a> and we'll respond within 30 days.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about this policy: <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>.
        </p>
      </Section>
    </div>
  </div>
);

export default Privacy;
